import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, DollarSign, AlertTriangle, FileText, Globe, Package, BarChart3, FileCheck, FileClock, Search, RefreshCw, SlidersHorizontal } from 'lucide-react';
import MissingDocTypesModal from './MissingDocTypesModal';
import MissingContractBreakdownModal from './MissingContractBreakdownModal';
import DiscrepancyBreakdownModal from './DiscrepancyBreakdownModal';
import CountryBreakdownModal from './CountryBreakdownModal';
import FilterDrawer from './FilterDrawer';
import TopServicesModal from './TopServicesModal';
import GroupedServicesDetailsModal from './GroupedServicesDetailsModal';
import './FinanceDashboard.css';
import { FiArrowRight } from "react-icons/fi";
import { IoIosArrowForward } from "react-icons/io";
import { Tooltip, Box, CircularProgress } from "@mui/material";
import { useQuery } from '@tanstack/react-query';
import { fetchRemovedServices } from '../../api/dashboardApi';
import RemovedServicesModal from './RemovedServicesModal';

const FinanceDashboard = ({ 
  dashboardData = {}, 
  formatNumber = null, 
  dateRange, 
  setDateRange, 
  activeDatePreset,
  selectedVendor, 
  selectedLoc, 
  setSelectedLoc,
  country,
  setSelectedCountry,
  selectedCountry,
  locations,
  setFilterLocation,
  filterLocation,
  canViewLocation,
  refetch,
  vendorName = "Vendor Name",
  onRefresh,
  isRefreshing = false,
  onOpenFilter,
  canViewDashboard = false,
  children
}) => {
  const navigate = useNavigate();
  const [animatedCards, setAnimatedCards] = useState([]);
  const [activeTimeFrames, setActiveTimeFrames] = useState({
    spend: '6M',
    discrepancy: '6M',
    invoices: '6M'
  });
  const [openModal, setOpenModal] = useState(false);
  const [activeCard, setActiveCard] = useState(null);
  const [missingDocsModalOpen, setMissingDocsModalOpen] = useState(false);
  const [missingContractModalOpen, setMissingContractModalOpen] = useState(false);
  const [sofStatus, setSofStatus] = useState(null);
  const [discrepancyModalOpen, setDiscrepancyModalOpen] = useState(false);
  const [discrepancyType, setDiscrepancyType] = useState(null);
  const [removedServicesModalOpen, setRemovedServicesModalOpen] = useState(false);
  const [discrepancyViewTab, setDiscrepancyViewTab] = useState('grouped');
  const [topServicesModalOpen, setTopServicesModalOpen] = useState(false);
  const [groupedServicesModalOpen, setGroupedServicesModalOpen] = useState(false);
  const [groupedServicesSearchFor, setGroupedServicesSearchFor] = useState('all');
  const [spendLocationType, setSpendLocationType] = useState('net');


  const removedServicesFilters = {};
  if (dateRange?.start) removedServicesFilters.start_date = dateRange.start.format ? dateRange.start.format('YYYY-MM-DD') : dateRange.start;
  if (dateRange?.end) removedServicesFilters.end_date = dateRange.end.format ? dateRange.end.format('YYYY-MM-DD') : dateRange.end;
  
  const currentCountry = selectedCountry || country;
  if (currentCountry && currentCountry !== "All") removedServicesFilters.country = currentCountry.toLowerCase();

  let loc = filterLocation?.id || filterLocation || selectedLoc;
  if (Array.isArray(loc)) {
    loc = loc.filter(l => l !== "All");
    if (loc.length === 0) loc = "All";
  }
  // Removed location_id from removedServicesFilters as per user request

  const vendor = selectedVendor?.id || selectedVendor;
  if (vendor && vendor !== -1) removedServicesFilters.vendor_id = vendor;

  const { data: removedServicesData, isLoading: loadingRemovedServices } = useQuery({
    queryKey: ["removedServicesCount", removedServicesFilters],
    queryFn: () => fetchRemovedServices(removedServicesFilters),
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    const cards = document.querySelectorAll('.fd-finance-card');
    cards.forEach((card, index) => {
      setTimeout(() => {
        setAnimatedCards(prev => [...prev, index]);
      }, index * 80);
    });
  }, []);

  const formatCurrency = formatNumber || ((value) => {
    const numValue = Number(value);
    if (isNaN(numValue)) return '$0.00';
    
    const absValue = Math.abs(numValue);
    if (absValue >= 1000000) {
      return `${(numValue / 1000000).toFixed(2)}M`;
    } else if (absValue >= 1000) {
      return `${(numValue / 1000).toFixed(2)}K`;
    }
    return `${numValue.toFixed(2)}`;
  });

  const handleNavigate = (url) => {
    if (url) navigate(url);
  };

  const handleCardClick = (cardKey, url) => {
    if (cardKey === 'spend' || cardKey === 'locations') {
      setActiveCard(cardKey);
      setOpenModal(true);
    } else if (url) {
      handleNavigate(url);
    }
  };

  const handleMissingContractClick = (status) => {
    setSofStatus(status);
    setMissingContractModalOpen(true);
  };

  const handleDiscrepancyClick = (type) => {
    setDiscrepancyType(type);
    setDiscrepancyModalOpen(true);
  };


  const TimeSelector = ({ cardId, periods = ['1M', '3M', '6M', '12M'] }) => {
    const active = activeTimeFrames[cardId] || '6M';

    return (
      <div className="fd-time-selector">
        {periods.map(period => (
          <Tooltip 
            key={period}
            title={getPeriodDateRange(getPeriodKey(period))?.replace(/[()]/g, '').trim()} 
            placement="top" 
            arrow
          >
            <div
              className={`fd-time-option ${active === period ? 'fd-active' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                setActiveTimeFrames(prev => ({ ...prev, [cardId]: period }));
              }}
            >
              {period}
            </div>
          </Tooltip>
        ))}
      </div>
    );
  };

  const ViewSelector = ({ active, onChange }) => (
    <div className="fd-view-selector">
      <div 
        className={`fd-view-option ${active === 'grouped' ? 'fd-active' : ''}`}
        onClick={(e) => { e.stopPropagation(); onChange('grouped'); }}
      >
        Grouped Service
      </div>
      <div 
        className={`fd-view-option ${active === 'individual' ? 'fd-active' : ''}`}
        onClick={(e) => { e.stopPropagation(); onChange('individual'); }}
      >
        Individual
      </div>
    </div>
  );

  const ProgressBar = ({ settled, remaining, total, settledTooltip, remainingTooltip, isCurrency = true }) => {
    const settledPercent = total > 0 ? (settled / total) * 100 : 0;
    const remainingPercent = total > 0 ? (remaining / total) * 100 : 0;

    const displaySettled = isCurrency ? formatCurrency(settled || 0) : (settled || 0);
    const displayRemaining = isCurrency ? formatCurrency(remaining || 0) : (remaining || 0);

    return (
      <div className="fd-progress-container">
        <div className="fd-progress-label">
          <Tooltip title={settledTooltip} placement="top" arrow>
            <span style={{ cursor: 'help' }}>Settled: {displaySettled}</span>
          </Tooltip>
          <Tooltip title={remainingTooltip} placement="top" arrow>
            <span style={{ cursor: 'help' }}>Remaining: {displayRemaining}</span>
          </Tooltip>
        </div>
        <div className="fd-progress-bar">
          <Tooltip title={settledTooltip} placement="top" arrow>
            <div 
              className="fd-progress-fill fd-progress-settled" 
              style={{ width: `${settledPercent}%`, cursor: 'help' }}
            />
          </Tooltip>
          <Tooltip title={remainingTooltip} placement="top" arrow>
            <div 
              className="fd-progress-fill fd-progress-remaining" 
              style={{ width: `${remainingPercent}%`, cursor: 'help' }}
            />
          </Tooltip>
        </div>
      </div>
    );
  };

  const getPeriodKey = (timeFrame) => {
    const mapping = {
      '1M': '1_month',
      '3M': '3_month',
      '6M': '6_month',
      '12M': '12_month'
    };
    return mapping[timeFrame];
  };

  const internalFormatNumber = formatNumber || ((num) => {
    const value = Number(num);
    if (!Number.isFinite(value)) return "0.00";
    const absValue = Math.abs(value);
    if (absValue >= 1_000_000) return (value / 1_000_000).toFixed(2).replace(/\.?0+$/, "") + "M";
    if (absValue >= 1_000) return (value / 1_000).toFixed(2).replace(/\.?0+$/, "") + "K";
    return value.toFixed(2);
  });

  const formatDateShort = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear().toString();
    return `${month} ${year}`;
  };

  const getPeriodDateRange = (periodKey) => {
    const periodData = dashboardData?.period_summary?.[periodKey];
    if (!periodData?.start_date || !periodData?.end_date) return '';
    if (periodKey === '1_month') {
      return ` (${formatDateShort(periodData.start_date)})`;
    }
    return ` (${formatDateShort(periodData.start_date)} - ${formatDateShort(periodData.end_date)})`;
  };

  const totalDifference = (dashboardData.total_amount_inv || 0) - (dashboardData.total_discrepancy || 0);

  return (
    <div className="fd-dashboard-container">
      <div className="fd-dashboard-grid">
        {/* Discrepancy Card - col-8 */}
        <div 
          className={`fd-finance-card ${animatedCards.includes(1) ? 'fd-animated' : ''}`}
          style={{ gridColumn: 'span 8', display: 'flex', flexDirection: 'column' }}
          // className={`fd-finance-card fd-clickable ${animatedCards.includes(1) ? 'fd-animated' : ''}`}
          // onClick={() => handleNavigate('/document?tab=invoice&search_for=discrepancy')}
        >
          {/* <div className="fd-card-header" style={{ marginBottom: '16px', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <h3 className="fd-card-title" style={{ margin: 0, fontSize: '16px' }}>Discrepancy Status</h3>
              <span className="fd-disc-header-badge">
                <AlertTriangle size={10} strokeWidth={3} />
                NEEDS REVIEW
              </span>
            </div>
          </div> */}

          <ViewSelector 
            active={discrepancyViewTab} 
            onChange={setDiscrepancyViewTab} 
          />

          {discrepancyViewTab === 'individual' ? (
            <>
              {/* ---- FULL WIDTH TOP SECTION: KPI + Sparkline ---- */}
              <div className="fd-disc-top-section">
                <div className="fd-disc-kpi-block">
                  <div className="fd-disc-kpi-label">TOTAL DISCREPANCY</div>
                  
                  <div className="fd-disc-kpi-value-row">
                    <Tooltip
                      title={
                        <div style={{ padding: '4px', maxWidth: '250px' }}>
                          <div style={{ marginBottom: '6px', fontSize: '14px', fontWeight: 'bold' }}>
                            {Number(dashboardData.total_discrepancy || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                          <div style={{ marginBottom: '6px', fontSize: '12px' }}>
                            Total amount of billing errors found in your invoices.
                          </div>
                          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '6px' }}>
                            Verification: Go to Document → Invoice and add up the values in the 'Total Discrepancy' column for the selected period.
                          </div>
                        </div>
                      }
                      arrow placement="top"
                    >
                      <div className="fd-disc-kpi-value" style={{ cursor: 'help' }}>
                        {formatCurrency(dashboardData.total_discrepancy)}
                      </div>
                    </Tooltip>
                    
                    <Tooltip 
                      title={
                        <div style={{ padding: '4px' }}>
                          <div style={{ marginBottom: '4px' }}>Total Spend: <b>{(dashboardData.total_amount_inv || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</b></div>
                          <div style={{ marginBottom: '4px' }}>- Total Discrepancy: <b>{(dashboardData.total_discrepancy || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</b></div>
                          <div style={{ borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '4px', marginTop: '4px' }}>
                            = Net Difference: <b>{((dashboardData.total_amount_inv || 0) - (dashboardData.total_discrepancy || 0)).toLocaleString(undefined, { minimumFractionDigits: 2 })}</b>
                          </div>
                        </div>
                      } 
                      arrow placement="top"
                    >
                      <div className="fd-disc-net-badge" style={{ cursor: 'help' }}>
                        <TrendingUp size={12} strokeWidth={3} />
                        {internalFormatNumber((dashboardData.total_amount_inv || 0) - (dashboardData.total_discrepancy || 0))}
                      </div>
                    </Tooltip>
                  </div>
                  <div className="fd-disc-subtext">Total amount of billing errors across all invoices</div>
                </div>

                <div className="fd-disc-sparkline">
                  <svg viewBox="0 0 100 30" width="100%" height="100%" preserveAspectRatio="none">
                    <path 
                      d="M 0 25 L 15 23 L 30 24 L 45 16 L 60 19 L 75 11 L 90 15 L 100 2" 
                      fill="none" 
                      stroke="#10b981" 
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <circle cx="100" cy="2" r="2" fill="#10b981" />
                  </svg>
                </div>
              </div>

              {/* ---- SETTLEMENT PROGRESS & BREAKDOWN ---- */}
              <div className="fd-disc-breakdown-section">
                {(() => {
                  const settled = dashboardData.total_settled || 0;
                  const remaining = dashboardData.remaining_discrepancy || 0;
                  const total = settled + remaining || 1; // prevent div by 0
                  const settledPct = (settled / total) * 100;
                  const remainingPct = (remaining / total) * 100;

                  return (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#6b7280', fontSize: '13px', marginBottom: '8px' }}>
                        <span>Settled: {internalFormatNumber(settled)}</span>
                        <span>Remaining: {internalFormatNumber(remaining)}</span>
                      </div>
                      <div className="fd-disc-progress-bar" style={{ height: '8px', marginBottom: '12px', borderRadius: '4px' }}>
                        <Tooltip title={`Settled: ${formatCurrency(settled)}`} arrow placement="top">
                          <div style={{ width: `${settledPct}%`, background: '#3b82f6', height: '100%' }} className="fd-disc-progress-positive" />
                        </Tooltip>
                        <Tooltip title={`Remaining: ${formatCurrency(remaining)}`} arrow placement="top">
                          <div style={{ width: `${remainingPct}%`, background: '#ef4444', height: '100%' }} className="fd-disc-progress-negative" />
                        </Tooltip>
                      </div>
                      
                      <div style={{ background: '#f9fafb', padding: '12px', borderRadius: '8px', display: 'flex', justifyContent: 'center', gap: '24px', fontSize: '12px', fontWeight: '600', color: '#4b5563' }}>
                        <div 
                          style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}
                          onClick={() => handleDiscrepancyClick('positive')}
                        >
                          <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#10b981' }} />
                          <span>POSITIVE:</span>
                          <span style={{ color: '#059669', fontSize: '14px', fontWeight: '700' }}>{Number(dashboardData.positive_total_discrepancy || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                        <div 
                          style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}
                          onClick={() => handleDiscrepancyClick('negative')}
                        >
                          <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ef4444' }} />
                          <span>NEGATIVE:</span>
                          <span style={{ color: '#dc2626', fontSize: '14px', fontWeight: '700' }}>{Number(dashboardData.negative_total_discrepancy || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>

              {/* ---- INDIVIDUAL STATISTICS ---- */}
              <div className="fd-disc-stats-section">
                <div className="fd-disc-stats-title">DISCREPANCY STATISTICS</div>
                <div className="fd-disc-stats-grid">
                  <Tooltip 
                    title={
                      <div style={{ padding: '4px', maxWidth: '250px' }}>
                        <div style={{ marginBottom: '6px', fontSize: '14px', fontWeight: 'bold' }}>
                          Settled: {Number(dashboardData.total_settled || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                        <div style={{ marginBottom: '6px', fontSize: '12px' }}>
                          Shows how much of the disputed amount is resolved.
                        </div>
                        <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '6px' }}>
                          Verification: Go to Document → Invoice and sum the 'Total Adjustment' column.
                        </div>
                      </div>
                    }
                    arrow placement="top"
                  >
                    <div className="fd-disc-card-new fd-disc-card-new--green">
                      <div className="fd-disc-card-new-left">
                        <div className="fd-disc-card-new-icon"><div className="dot" /></div>
                        <div className="fd-disc-card-new-info">
                          <div className="fd-disc-card-new-label">Settled Amount</div>
                          <div className="fd-disc-card-new-value">{formatCurrency(dashboardData.total_settled || 0)}</div>
                        </div>
                      </div>
                    </div>
                  </Tooltip>

                  <Tooltip 
                    title={
                      <div style={{ padding: '4px', maxWidth: '250px' }}>
                        <div style={{ marginBottom: '6px', fontSize: '14px', fontWeight: 'bold' }}>
                          Remaining: {Number(dashboardData.remaining_discrepancy || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                        <div style={{ marginBottom: '6px', fontSize: '12px' }}>
                          Shows how much of the disputed amount is pending.
                        </div>
                        <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '6px' }}>
                          Verification: Go to Document → Invoice and calculate Total Discrepancy minus Total Adjustment.
                        </div>
                      </div>
                    }
                    arrow placement="top"
                  >
                    <div className="fd-disc-card-new fd-disc-card-new--red">
                      <div className="fd-disc-card-new-left">
                        <div className="fd-disc-card-new-icon"><div className="dot" /></div>
                        <div className="fd-disc-card-new-info">
                          <div className="fd-disc-card-new-label">Remaining Amount</div>
                          <div className="fd-disc-card-new-value">{formatCurrency(dashboardData.remaining_discrepancy || 0)}</div>
                        </div>
                      </div>
                    </div>
                  </Tooltip>

                  <Tooltip 
                    title={
                      <div style={{ padding: '4px', maxWidth: '250px' }}>
                        <div style={{ marginBottom: '6px', fontSize: '14px', fontWeight: 'bold' }}>
                          {Number(dashboardData.missing_contract_amount?.total_discrepancy || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                        <div style={{ marginBottom: '6px', fontSize: '12px' }}>
                          Errors caused because a service wasn't found in a contract (SOF).
                        </div>
                        <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '6px' }}>
                          Verification: Click this row to open the 'Missing Contract Breakdown' modal and review the detailed list of affected invoices.
                        </div>
                      </div>
                    }
                    arrow placement="top"
                  >
                    <div 
                      className="fd-disc-card-new fd-disc-card-new--purple"
                      onClick={(e) => { e.stopPropagation(); handleMissingContractClick('SERVICE_NOT_IN_SOF'); }}
                    >
                      <div className="fd-disc-card-new-left">
                        <div className="fd-disc-card-new-icon"><div className="dot" /></div>
                        <div className="fd-disc-card-new-info">
                          <div className="fd-disc-card-new-label">Due to Missing Contract</div>
                          <div className="fd-disc-card-new-value">{formatCurrency(dashboardData.missing_contract_amount?.total_discrepancy || 0)}</div>
                        </div>
                      </div>
                      <div className="fd-disc-card-new-arrow"><IoIosArrowForward /></div>
                    </div>
                  </Tooltip>

                  <Tooltip 
                    title={
                      <div style={{ padding: '4px', maxWidth: '250px' }}>
                        <div style={{ marginBottom: '6px', fontSize: '14px', fontWeight: 'bold' }}>
                          {Number((dashboardData.total_discrepancy || 0) - (dashboardData.missing_contract_amount?.total_discrepancy || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                        <div style={{ marginBottom: '6px', fontSize: '12px' }}>
                          Errors caused by a mismatch with existing contract rates.
                        </div>
                        <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '6px' }}>
                          Verification: Click this row to open the 'Contract Mismatch' modal to validate against matched SOF rates.
                        </div>
                      </div>
                    }
                    arrow placement="top"
                  >
                    <div 
                      className="fd-disc-card-new fd-disc-card-new--gray"
                      onClick={(e) => { e.stopPropagation(); handleMissingContractClick('SERVICE_IN_SOF'); }}
                    >
                      <div className="fd-disc-card-new-left">
                        <div className="fd-disc-card-new-icon"><div className="dot" /></div>
                        <div className="fd-disc-card-new-info">
                          <div className="fd-disc-card-new-label">Based on Contract Amount</div>
                          <div className="fd-disc-card-new-value">{formatCurrency((dashboardData.total_discrepancy || 0) - (dashboardData.missing_contract_amount?.total_discrepancy || 0))}</div>
                        </div>
                      </div>
                      <div className="fd-disc-card-new-arrow"><IoIosArrowForward /></div>
                    </div>
                  </Tooltip>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* ---- FULL WIDTH TOP SECTION: KPI + Sparkline ---- */}
              <div className="fd-disc-top-section">
                <div className="fd-disc-kpi-block">
                  <div className="fd-disc-kpi-label">TOTAL DISCREPANCY</div>
                  
                  <div className="fd-disc-kpi-value-row">
                    <Tooltip
                      title={
                        <div style={{ padding: '4px', maxWidth: '250px' }}>
                          <div style={{ marginBottom: '6px', fontSize: '14px', fontWeight: 'bold' }}>
                            {Number(dashboardData?.grouped_service_stats?.total_discrepancy || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                          <div style={{ marginBottom: '6px', fontSize: '12px' }}>
                            Total errors calculated by grouping similar services together.
                          </div>
                          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '6px' }}>
                            Verification: Click 'View Service Discrepancies Details' below and sum up the Discrepancy Amount column.
                          </div>
                        </div>
                      }
                      arrow placement="top"
                    >
                      <div className="fd-disc-kpi-value" style={{ cursor: 'help' }}>
                        {formatCurrency(dashboardData?.grouped_service_stats?.total_discrepancy || 0)}
                      </div>
                    </Tooltip>
                    
                    <Tooltip 
                      title={
                        <div style={{ padding: '4px' }}>
                          <div style={{ marginBottom: '4px' }}>Total Spend: <b>{(dashboardData.total_amount_inv || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</b></div>
                          <div style={{ marginBottom: '4px' }}>- Grouped Discrepancy: <b>{(dashboardData?.grouped_service_stats?.total_discrepancy || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</b></div>
                          <div style={{ borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '4px', marginTop: '4px' }}>
                            = Net Difference: <b>{((dashboardData.total_amount_inv || 0) - (dashboardData?.grouped_service_stats?.total_discrepancy || 0)).toLocaleString(undefined, { minimumFractionDigits: 2 })}</b>
                          </div>
                        </div>
                      } 
                      arrow placement="top"
                    >
                      <div className="fd-disc-net-badge" style={{ cursor: 'help' }}>
                        <TrendingUp size={12} strokeWidth={3} />
                        {internalFormatNumber((dashboardData.total_amount_inv || 0) - (dashboardData?.grouped_service_stats?.total_discrepancy || 0))}
                      </div>
                    </Tooltip>
                  </div>
                  <div className="fd-disc-subtext">Aggregated discrepancy for grouped services</div>
                </div>

                {/* Decorative Sparkline */}
                {/* <div className="fd-disc-sparkline">
                  <svg viewBox="0 0 100 30" width="100%" height="100%" preserveAspectRatio="none">
                    <path 
                      d="M 0 25 L 15 23 L 30 24 L 45 16 L 60 19 L 75 11 L 90 15 L 100 2" 
                      fill="none" 
                      stroke="#10b981" 
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <circle cx="100" cy="2" r="2" fill="#10b981" />
                  </svg>
                </div> */}
              </div>

              {/* ---- NET DIFFERENCE BREAKDOWN ---- */}
              <div className="fd-disc-breakdown-section">
                <div className="fd-disc-breakdown-title">NET DIFFERENCE BREAKDOWN</div>
                {(() => {
                  const pos = Math.abs(dashboardData?.grouped_service_stats?.positive_discrepancy || 0);
                  const neg = Math.abs(dashboardData?.grouped_service_stats?.negative_discrepancy || 0);
                  const tot = pos + neg || 1; // prevent div by 0
                  const negPct = (neg / tot) * 100;
                  const posPct = (pos / tot) * 100;

                  return (
                    <>
                      <div className="fd-disc-progress-bar">
                        <Tooltip title={`Negative Discrepancy: ${formatCurrency(dashboardData?.grouped_service_stats?.negative_discrepancy || 0)}`} arrow placement="top">
                          <div className="fd-disc-progress-negative" style={{ width: `${negPct}%` }} />
                        </Tooltip>
                        <Tooltip title={`Positive Discrepancy: ${formatCurrency(dashboardData?.grouped_service_stats?.positive_discrepancy || 0)}`} arrow placement="top">
                          <div className="fd-disc-progress-positive" style={{ width: `${posPct}%` }} />
                        </Tooltip>
                      </div>
                      <div className="fd-disc-progress-labels">
                        <Tooltip title={
                          <div style={{ padding: '4px', maxWidth: '250px' }}>
                            <div style={{ marginBottom: '6px', fontSize: '14px', fontWeight: 'bold' }}>
                              {Number(dashboardData?.grouped_service_stats?.negative_discrepancy || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </div>
                            <div style={{ marginBottom: '6px', fontSize: '12px' }}>
                              Grouped billing errors favoring the vendor (Negative).
                            </div>
                            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '6px' }}>
                              Verification: Click 'View Service Discrepancies Details' and sum up the negative values in the Discrepancy Amount column, or click on 'Negative' to open the filtered list.
                            </div>
                          </div>
                        } arrow placement="top">
                          <div 
                            className="fd-disc-progress-label negative" 
                            style={{ cursor: 'pointer' }}
                            onClick={() => { setGroupedServicesSearchFor('underbilled'); setGroupedServicesModalOpen(true); }}
                          >
                            <div className="dot" />
                            <span>Negative <span className="fd-disc-progress-val">{internalFormatNumber(dashboardData?.grouped_service_stats?.negative_discrepancy || 0)}</span></span>
                          </div>
                        </Tooltip>
                        <Tooltip title={
                          <div style={{ padding: '4px', maxWidth: '250px' }}>
                            <div style={{ marginBottom: '6px', fontSize: '14px', fontWeight: 'bold' }}>
                              {Number(dashboardData?.grouped_service_stats?.positive_discrepancy || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </div>
                            <div style={{ marginBottom: '6px', fontSize: '12px' }}>
                              Grouped billing errors favoring us (Positive).
                            </div>
                            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '6px' }}>
                              Verification: Click 'View Service Discrepancies Details' and sum up the positive values in the Discrepancy Amount column, or click on 'Positive' to open the filtered list.
                            </div>
                          </div>
                        } arrow placement="top">
                          <div 
                            className="fd-disc-progress-label positive"
                            style={{ cursor: 'pointer' }}
                            onClick={() => { setGroupedServicesSearchFor('overbilled'); setGroupedServicesModalOpen(true); }}
                          >
                            <span><span className="fd-disc-progress-val">{internalFormatNumber(dashboardData?.grouped_service_stats?.positive_discrepancy || 0)}</span> Positive</span>
                            <div className="dot" />
                          </div>
                        </Tooltip>
                      </div>
                    </>
                  );
                })()}
              </div>

              {/* ---- SERVICE GROUP STATISTICS ---- */}
              <div className="fd-disc-stats-section">
                <div className="fd-disc-stats-title">SERVICE GROUP STATISTICS</div>
                <div className="fd-disc-stats-grid">
                  <Tooltip title={
                        <div style={{ padding: '4px', maxWidth: '250px' }}>
                          <div style={{ marginBottom: '6px', fontSize: '14px', fontWeight: 'bold' }}>
                            {dashboardData?.grouped_service_stats?.service_count_with_discrepancy || 0}
                          </div>
                          <div style={{ marginBottom: '6px', fontSize: '12px' }}>
                            Number of service groups containing billing errors.
                          </div>
                          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '6px' }}>
                            Verification: Open the 'Service Discrepancies Details' modal and count the number of unique service rows.
                          </div>
                        </div>
                      } arrow placement="top">
                        <div 
                          className="fd-disc-card-new fd-disc-card-new--gray"
                          onClick={() => { setGroupedServicesSearchFor('all'); setGroupedServicesModalOpen(true); }}
                        >
                          <div className="fd-disc-card-new-left">
                            <div className="fd-disc-card-new-icon"><div className="dot" /></div>
                            <div className="fd-disc-card-new-info">
                              <div className="fd-disc-card-new-label">Services with Discrepancy</div>
                              <div className="fd-disc-card-new-value">{dashboardData?.grouped_service_stats?.service_count_with_discrepancy || 0}</div>
                            </div>
                          </div>
                          <div className="fd-disc-card-new-arrow"><IoIosArrowForward /></div>
                        </div>
                      </Tooltip>
    
                      <Tooltip title={
                        <div style={{ padding: '4px', maxWidth: '250px' }}>
                          <div style={{ marginBottom: '6px', fontSize: '14px', fontWeight: 'bold' }}>
                            {dashboardData?.grouped_service_stats?.overbilled_count || 0}
                          </div>
                          <div style={{ marginBottom: '6px', fontSize: '12px' }}>
                            Count of services billed higher than the contract.
                          </div>
                          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '6px' }}>
                            Verification: Click on the same to see the details.
                          </div>
                        </div>
                      } arrow placement="top">
                        <div 
                          className="fd-disc-card-new fd-disc-card-new--red"
                          onClick={() => { setGroupedServicesSearchFor('overbilled'); setGroupedServicesModalOpen(true); }}
                        >
                          <div className="fd-disc-card-new-left">
                            <div className="fd-disc-card-new-icon"><div className="dot" /></div>
                            <div className="fd-disc-card-new-info">
                              <div className="fd-disc-card-new-label">Overbilled</div>
                              <div className="fd-disc-card-new-value">{dashboardData?.grouped_service_stats?.overbilled_count || 0}</div>
                            </div>
                          </div>
                          <div className="fd-disc-card-new-arrow"><IoIosArrowForward /></div>
                        </div>
                      </Tooltip>
    
                      <Tooltip title={
                        <div style={{ padding: '4px', maxWidth: '250px' }}>
                          <div style={{ marginBottom: '6px', fontSize: '14px', fontWeight: 'bold' }}>
                            {dashboardData?.grouped_service_stats?.underbilled_count || 0}
                          </div>
                          <div style={{ marginBottom: '6px', fontSize: '12px' }}>
                            Count of services billed lower than the contract.
                          </div>
                          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '6px' }}>
                            Verification: Click on the same to see the details.
                          </div>
                        </div>
                      } arrow placement="top">
                        <div 
                          className="fd-disc-card-new fd-disc-card-new--green"
                          onClick={() => { setGroupedServicesSearchFor('underbilled'); setGroupedServicesModalOpen(true); }}
                        >
                          <div className="fd-disc-card-new-left">
                            <div className="fd-disc-card-new-icon"><div className="dot" /></div>
                            <div className="fd-disc-card-new-info">
                              <div className="fd-disc-card-new-label">Underbilled</div>
                              <div className="fd-disc-card-new-value">{dashboardData?.grouped_service_stats?.underbilled_count || 0}</div>
                            </div>
                          </div>
                          <div className="fd-disc-card-new-arrow"><IoIosArrowForward /></div>
                        </div>
                      </Tooltip>
    
                      <Tooltip title={
                        <div style={{ padding: '4px', maxWidth: '250px' }}>
                          <div style={{ marginBottom: '6px', fontSize: '14px', fontWeight: 'bold' }}>
                            {Number(dashboardData?.grouped_service_stats?.due_to_missing_contract || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                          <div style={{ marginBottom: '6px', fontSize: '12px' }}>
                            Discrepancy amount for grouped services with a missing contract.
                          </div>
                          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '6px' }}>
                            Verification: Click on the same to see the details.
                          </div>
                        </div>
                      } arrow placement="top">
                        <div 
                          className="fd-disc-card-new fd-disc-card-new--purple"
                          onClick={() => { setGroupedServicesSearchFor('ARTIFACT_UNRESOLVED_CONTRACT'); setGroupedServicesModalOpen(true); }}
                        >
                          <div className="fd-disc-card-new-left">
                            <div className="fd-disc-card-new-icon"><div className="dot" /></div>
                            <div className="fd-disc-card-new-info">
                              <div className="fd-disc-card-new-label">Due to Missing Contract</div>
                              <div className="fd-disc-card-new-value">{formatCurrency(dashboardData?.grouped_service_stats?.due_to_missing_contract || 0)}</div>
                            </div>
                          </div>
                          <div className="fd-disc-card-new-arrow"><IoIosArrowForward /></div>
                        </div>
                      </Tooltip>
                </div>
              </div>

              <div className="fd-card-footer">
                <button
                  type="button"
                  className="fd-view-action--full"
                  onClick={(e) => { e.stopPropagation(); setGroupedServicesSearchFor('all'); setGroupedServicesModalOpen(true); }}
                >
                  View Service Discrepancies Details
                  <FiArrowRight size={14} style={{ marginLeft: '4px' }} />
                </button>
              </div>
            </>
          )}
        </div>

        {/* Services Card - col-4 */}
        <div className={`fd-finance-card ${animatedCards.includes(4) ? 'fd-animated' : ''}`} style={{ gridColumn: 'span 4' }}>
          <div className="fd-card-header">
            <h3 className="fd-card-title">Services Overview</h3>
            <div className="fd-card-icon fd-icon-success">
              <Package size={18} />
            </div>
          </div>
          <div className="fd-kpi-value">{dashboardData.total_services || 0}</div>
          <div className="fd-kpi-label">Total services tracked across all locations</div>

          <div className="fd-services-grid">
            <Tooltip
              title={
                <div style={{ padding: '4px', maxWidth: '250px' }}>
                  <div style={{ marginBottom: '6px', fontSize: '12px' }}>
                    Total number of unique services billed across all processed invoices. Each service is counted once, even if it appears in multiple invoices or locations.
                  </div>
                  <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '6px' }}>
                    Verification: Go to Document → Invoice, open the Invoice Details tab, and count the unique values in the Service ID column.
                  </div>
                </div>
              }
              arrow
              placement="top"
            >
              <div className="fd-location-item fd-icon-primary service-card" style={{ cursor: 'help' }}>
                <div className="fd-location-name">
                  <span className="fd-flag">
                    <Package size={20} />
                  </span>
                  <span>Total Services</span>
                </div>
                <span className="">{dashboardData.total_services || 0}</span>
              </div>
            </Tooltip>

            <Tooltip
              title={
                <div style={{ padding: '4px', maxWidth: '250px' }}>
                  <div style={{ fontWeight: 600, marginBottom: '4px' }}>Locations with Billing</div>
                  <div style={{ marginBottom: '6px', fontSize: '12px' }}>
                    Number of unique location IDs that have at least one billed service line in a processed invoice during the selected date range.
                  </div>
                </div>
              }
              arrow
              placement="top"
            >
              <div className="fd-location-item fd-icon-success service-card" style={{ cursor: 'help' }}>
                <div className="fd-location-name">
                  <span className="fd-flag">
                     <Globe size={20} />
                  </span>
                  <span>Billed Location IDs</span>
                </div>
                <span className="">{dashboardData.total_locations || 0}</span>
              </div>
            </Tooltip>

            <div 
              className="fd-location-item fd-icon-warning service-card fd-clickable-item"
              onClick={(e) => {
                e.stopPropagation();
                handleNavigate('/vendor');
              }}
            >
              <div className="fd-location-name">
                <span className="fd-flag">
                  <FileText size={20} />
                </span>
                <span>Vendors</span>
              </div>
              <Tooltip
                title={
                  <div style={{ padding: '4px', maxWidth: '250px' }}>
                    <div style={{ marginBottom: '6px', fontSize: '12px' }}>
                      Total number of unique vendor names found across all processed invoices.
                    </div>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '6px' }}>
                      Verification: Go to Document → Invoice and count the unique values in the Vendor column. Cross-check with the Vendor tab total.
                    </div>
                  </div>
                }
                arrow
                placement="top"
              >
                <span className="" style={{ cursor: 'help' }}>{dashboardData.vendor_count || 0}
                          <span className="fd-arrow-icon">
        <IoIosArrowForward />
      </span>
                </span>
              </Tooltip>
            </div>

            <Tooltip
              title={
                <div style={{ padding: '4px', maxWidth: '250px' }}>
                  <div style={{ marginBottom: '6px', fontSize: '12px' }}>
                    Total number of Service Order Forms (SOFs) uploaded into the system, regardless of their current status.
                  </div>
                  <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '6px' }}>
                    Verification: Go to Document → SOF and check the total row count at the bottom of the table.
                  </div>
                </div>
              }
              arrow
              placement="top"
            >
              <div 
                className="fd-location-item fd-icon-sof service-card fd-clickable-item"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open('/document?tab=sof', '_blank', 'noopener,noreferrer,width=1200,height=800');
                }}
              >
                <div className="fd-location-name">
                  <span className="fd-flag">
                    <FileCheck size={20} />
                  </span>
                  <span>Total SOF</span>
                </div>
                <span>{dashboardData.total_sof || 0}
                          <span className="fd-arrow-icon">
        <IoIosArrowForward />
      </span>
                </span>
              </div>
            </Tooltip>

            <Tooltip
              title={
                <div style={{ padding: '4px', maxWidth: '250px' }}>
                  <div style={{ marginBottom: '6px', fontSize: '12px' }}>
                    Total number of SOFs that have been uploaded but are still waiting to be processed.
                  </div>
                  <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '6px' }}>
                    Verification: Go to Document → SOF and filter by Status = Pending. The resulting row count should match this number.
                  </div>
                </div>
              }
              arrow
              placement="top"
            >
              <div 
                className="fd-location-item fd-icon-danger service-card " style={{ cursor: 'help' }}
              >
                <div className="fd-location-name">
                  <span className="fd-flag">
                    <FileClock size={20} />
                  </span>
                  <span>Pending SOF</span>
                </div>
                <span>{dashboardData.total_pending_sof || 0}</span>
              </div>
            </Tooltip>

            <Tooltip
              title={
                <div style={{ padding: '4px', maxWidth: '250px' }}>
                  {dashboardData.total_missing_doc_discrepancy > 0 && (
                    <div style={{ marginBottom: '8px', fontSize: '13px', fontWeight: 'bold' }}>
                      Total Missing Discrepancy: {(dashboardData.total_missing_doc_discrepancy || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </div>
                  )}
                  <div style={{ marginBottom: '6px', fontSize: '12px' }}>
                    Number of SOF/document references found in invoice line items that do not have a matching SOF uploaded in the system yet.
                  </div>
                  <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '6px' }}>
                    Verification: Click the card to open the Pending Documents modal and count the listed document IDs. To verify manually: look for invoice line items in Document → Invoice whose Document ID does not appear in Document → SOF.
                  </div>
                </div>
              }
              arrow
              placement="top"
            >
              <div 
                className="fd-location-item fd-icon-missing service-card fd-clickable-item"
                onClick={() => setMissingDocsModalOpen(true)}
              >
                <div className="fd-location-name">
                  <span className="fd-flag">
                    <Search size={20} />
                  </span>
                  <span>Pending Documents</span>
                </div>
                <span className="">
                  {dashboardData.missing_doc_type_count || 0}
                  {dashboardData.total_missing_doc_discrepancy > 0 && (
                    <span style={{ marginLeft: '8px', color: '#7b1fa2', fontWeight: 600, fontSize: '0.9rem' }}>
                      ({internalFormatNumber(dashboardData.total_missing_doc_discrepancy)})
                    </span>
                  )}
                  <span className="fd-arrow-icon">
                    <IoIosArrowForward />
                  </span>
                </span>
              </div>
            </Tooltip>

            <Tooltip
              title={
                <div style={{ padding: '4px', maxWidth: '250px' }}>
                  <div style={{ marginBottom: '6px', fontSize: '12px' }}>
                    Count of services that have been formally cancelled or disconnected, as recorded in a SOF with Activity marked as 'Removed'.
                  </div>
                  <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '6px' }}>
                    Verification: Click the card to open the Removed Services modal and check the total count. To verify manually: go to Document → SOF and filter line items where Activity = Removed.
                  </div>
                </div>
              }
              arrow
              placement="top"
            >
              <div 
                className="fd-location-item fd-icon-danger service-card fd-clickable-item"
                onClick={() => setRemovedServicesModalOpen(true)}
              >
                <div className="fd-location-name">
                  <span className="fd-flag">
                    <FileClock size={20} />
                  </span>
                  <span>Removed Services</span>
                </div>
                <span className="">
                  {loadingRemovedServices ? (
                    <CircularProgress size={16} sx={{ color: "#7C2EA0" }} />
                  ) : (
                    removedServicesData?.count || 0
                  )}
                  <span className="fd-arrow-icon">
                    <IoIosArrowForward />
                  </span>
                </span>
              </div>
            </Tooltip>
          </div>
        </div>
        
        {/* Total Spend Card - col-4 */}
        <div 
          className={`fd-finance-card ${animatedCards.includes(0) ? 'fd-animated' : ''}`}
          style={{ gridColumn: 'span 4' }}
        >
          <div className="fd-card-header">
            <h3 className="fd-card-title">Total Spend</h3>
            <div className="fd-card-icon fd-icon-primary">
              <DollarSign size={18} />
            </div>
          </div>
          <Tooltip
            title={
              <div style={{ padding: '4px', maxWidth: '250px' }}>
                <div style={{ marginBottom: '6px', fontSize: '14px', fontWeight: 'bold' }}>
                  {Number(dashboardData.total_net_amount_inv || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div style={{ marginBottom: '6px', fontSize: '12px' }}>
                  Total amount of money billed across all your invoices.
                </div>
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '6px' }}>
                  Verification: Go to Document → Invoice and add up the values in the 'Total USD' column for the selected period.
                </div>
              </div>
            }
            arrow
            placement="top"
          >
            <div className="fd-kpi-value" style={{ cursor: 'help', display: 'inline-block' }}>{formatCurrency(dashboardData.total_net_amount_inv)}</div>
          </Tooltip>
          <div className="fd-kpi-label">Cumulative spending across all periods</div>
          


          {/* 
          <TimeSelector cardId="spend" />
          
          <div className="fd-compact-data">
            <div className="fd-data-item">
              <span className="fd-data-label">
                {activeTimeFrames.spend} Period
                <span style={{ fontSize: '0.85em', fontWeight: 'normal', marginLeft: '4px', opacity: 0.8 }}>
                  {getPeriodDateRange(getPeriodKey(activeTimeFrames.spend))}
                </span>
              </span>
              <Tooltip
                title={
                  <div style={{ padding: '4px', maxWidth: '250px' }}>
                    <div style={{ marginBottom: '6px', fontSize: '14px', fontWeight: 'bold' }}>
                      {Number(dashboardData.period_summary?.[getPeriodKey(activeTimeFrames.spend)]?.total_spend || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    <div style={{ marginBottom: '6px', fontSize: '12px' }}>
                      Total spending for the currently selected time period.
                    </div>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '6px' }}>
                      Verification: Go to Document → Invoice and add up the values in the 'Sum Amount USD' column for {getPeriodDateRange(getPeriodKey(activeTimeFrames.spend))?.replace(/[()]/g, '').trim() || "the selected period"}.
                    </div>
                  </div>
                }
                arrow
                placement="top"
              >
                <span className="fd-data-value" style={{ cursor: 'help' }}>
                  {formatCurrency(dashboardData.period_summary?.[getPeriodKey(activeTimeFrames.spend)]?.total_spend || 0)}
                </span>
              </Tooltip>
            </div>
          </div>
          */}

          <div className="fd-expandable-section">
            <div className="fd-section-header">
              <div className="fd-section-title">Recent Activity</div>
            </div>
            <ul className="fd-detail-list">
              <li className="fd-detail-item">
                <span className="fd-detail-label">
                  Last Month
                  <span style={{ fontSize: '0.85em', fontWeight: 'normal', marginLeft: '4px', opacity: 0.8 }}>
                    {getPeriodDateRange('1_month')}
                  </span>
                </span>
                <Tooltip
                  title={
                    <div style={{ padding: '4px', maxWidth: '250px' }}>
                      <div style={{ marginBottom: '6px', fontSize: '14px', fontWeight: 'bold' }}>
                        {Number(dashboardData.period_summary?.['1_month']?.total_net_spend || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                      <div style={{ marginBottom: '6px', fontSize: '12px' }}>
                        Total spending for the previous calendar month.
                      </div>
                      <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '6px' }}>
                        Verification: Go to Document → Invoice and add up the values in the 'Total USD' column for {getPeriodDateRange('1_month')?.replace(/[()]/g, '').trim() || "the previous month"}.
                      </div>
                    </div>
                  }
                  arrow
                  placement="top"
                >
                  <span className="fd-detail-value" style={{ cursor: 'help' }}>{formatCurrency(dashboardData.period_summary?.['1_month']?.total_net_spend || 0)}</span>
                </Tooltip>
              </li>
              <li className="fd-detail-item">
                <span className="fd-detail-label">
                  Last Quarter
                  <span style={{ fontSize: '0.85em', fontWeight: 'normal', marginLeft: '4px', opacity: 0.8 }}>
                    {getPeriodDateRange('3_month')}
                  </span>
                </span>
                <Tooltip
                  title={
                    <div style={{ padding: '4px', maxWidth: '250px' }}>
                      <div style={{ marginBottom: '6px', fontSize: '14px', fontWeight: 'bold' }}>
                        {Number(dashboardData.period_summary?.['3_month']?.total_net_spend || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                      <div style={{ marginBottom: '6px', fontSize: '12px' }}>
                        Total spending over the last 3 months.
                      </div>
                      <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '6px' }}>
                        Verification: Go to Document → Invoice and add up the values in the 'Total USD' column for {getPeriodDateRange('3_month')?.replace(/[()]/g, '').trim() || "the previous quarter"}.
                      </div>
                    </div>
                  }
                  arrow
                  placement="top"
                >
                  <span className="fd-detail-value" style={{ cursor: 'help' }}>{formatCurrency(dashboardData.period_summary?.['3_month']?.total_net_spend || 0)}</span>
                </Tooltip>
              </li>
              <li className="fd-detail-item">
                <span className="fd-detail-label">
                  Last 12 month
                  <span style={{ fontSize: '0.85em', fontWeight: 'normal', marginLeft: '4px', opacity: 0.8 }}>
                    {getPeriodDateRange('12_month')}
                  </span>
                </span>
                <Tooltip
                  title={
                    <div style={{ padding: '4px', maxWidth: '250px' }}>
                      <div style={{ marginBottom: '6px', fontSize: '14px', fontWeight: 'bold' }}>
                        {Number(dashboardData.period_summary?.['12_month']?.total_net_spend || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                      <div style={{ marginBottom: '6px', fontSize: '12px' }}>
                        Total spending over the last 12 months.
                      </div>
                      <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '6px' }}>
                        Verification: Go to Document → Invoice and add up the values in the 'Total USD' column for {getPeriodDateRange('12_month')?.replace(/[()]/g, '').trim() || "the previous 12 months"}.
                      </div>
                    </div>
                  }
                  arrow
                  placement="top"
                >
                  <span className="fd-detail-value" style={{ cursor: 'help' }}>{formatCurrency(dashboardData.period_summary?.['12_month']?.total_net_spend || 0)}</span>
                </Tooltip>
              </li>
            </ul>
          </div>
          <div className="fd-card-footer">
            <Tooltip
              title={
                <div style={{ padding: '4px', maxWidth: '250px' }}>
                  <div style={{ marginBottom: '6px', fontSize: '12px' }}>
                    Shows a detailed breakdown of your total spend grouped by each country.
                  </div>
                  {/* <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '6px' }}>
                    Verification: Open the popup and compare the Country/Spend rows against a grouped export from the Invoice tab filtered by Country.
                  </div> */}
                </div>
              }
              arrow
              placement="top"
            >
              <button
                type="button"
                className="fd-view-action fd-view-action--small"
                onClick={() => handleCardClick("spend")}
              >
                View Details
                <FiArrowRight size={11} className='fd-arrow-icon' />
              </button>
            </Tooltip>
          </div>
        </div>

        {/* Invoice Health Card - col-4 */}
        <div 
          className={`fd-finance-card ${animatedCards.includes(2) ? 'fd-animated' : ''}`}
          style={{ gridColumn: 'span 4' }}
          // className={`fd-finance-card fd-clickable ${animatedCards.includes(2) ? 'fd-animated' : ''}`}
          // onClick={() => handleNavigate('/document?tab=invoice&search_for=discrepancy')}
        >
          <div className="fd-card-header">
            <h3 className="fd-card-title">Invoice Health</h3>
            <div className="fd-card-icon fd-icon-success">
              <FileText size={18} />
            </div>
          </div>
          <Tooltip
            title={
              <div style={{ padding: '4px', maxWidth: '250px' }}>
                <div style={{ marginBottom: '6px', fontSize: '14px', fontWeight: 'bold' }}>
                  {dashboardData.invoice_with_discrepancy || 0}
                </div>
                <div style={{ marginBottom: '6px', fontSize: '12px' }}>
                  The number of invoices that contain billing errors.
                </div>
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '6px' }}>
                  Verification: Go to Document → Invoice and filter for 'Total Discrepancy != 0', then check the row count.
                </div>
              </div>
            }
            arrow
            placement="top"
          >
            <div className="fd-kpi-value" style={{ cursor: 'help', display: 'inline-block' }}>{dashboardData.invoice_with_discrepancy || 0}</div>
          </Tooltip>
          <div className="fd-kpi-label">Invoices with discrepancies</div>
          
          <ProgressBar 
            isCurrency={false}
            settled={(dashboardData.total_invoices || 0) - (dashboardData.invoice_with_discrepancy || 0)}
            remaining={dashboardData.invoice_with_discrepancy || 0}
            total={dashboardData.total_invoices || 1}
            settledTooltip={
              <div style={{ padding: '4px', maxWidth: '250px' }}>
                <div style={{ marginBottom: '6px', fontSize: '14px', fontWeight: 'bold' }}>
                  Settled: {(dashboardData.total_invoices || 0) - (dashboardData.invoice_with_discrepancy || 0)}
                </div>
                <div style={{ marginBottom: '6px', fontSize: '12px' }}>
                  Compares perfectly fine invoices against those with errors.
                </div>
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '6px' }}>
                  Verification: Go to Document → Invoice and calculate Total Invoices minus Invoices with Discrepancies.
                </div>
              </div>
            }
            remainingTooltip={
              <div style={{ padding: '4px', maxWidth: '250px' }}>
                <div style={{ marginBottom: '6px', fontSize: '14px', fontWeight: 'bold' }}>
                  Remaining: {dashboardData.invoice_with_discrepancy || 0}
                </div>
                <div style={{ marginBottom: '6px', fontSize: '12px' }}>
                  Compares perfectly fine invoices against those with errors.
                </div>
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '6px' }}>
                  Verification: Go to Document → Invoice and filter for 'Final Discrepancy != 0', then check the row count.
                </div>
              </div>
            }
          />
          
          {/* 
          <TimeSelector cardId="invoices" />
          
          <div className="fd-compact-data">
            <div className="fd-data-item">
              <span className="fd-data-label">
                {activeTimeFrames.invoices} Period
                <span style={{ fontSize: '0.85em', fontWeight: 'normal', marginLeft: '4px', opacity: 0.8 }}>
                  {getPeriodDateRange(getPeriodKey(activeTimeFrames.invoices))}
                </span>
              </span>
              <Tooltip
                title={
                  <div style={{ padding: '4px', maxWidth: '250px' }}>
                    <div style={{ marginBottom: '6px', fontSize: '14px', fontWeight: 'bold' }}>
                      {dashboardData.period_summary?.[getPeriodKey(activeTimeFrames.invoices)]?.invoice_discrepancy_count || 0}
                    </div>
                    <div style={{ marginBottom: '6px', fontSize: '12px' }}>
                      Number of invoices with errors in the selected timeframe.
                    </div>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '6px' }}>
                      Verification: Go to Document → Invoice and set the date filter to the selected period, filter for 'Total Discrepancy != 0', then check the row count.
                    </div>
                  </div>
                }
                arrow
                placement="top"
              >
                <span className="fd-data-value" style={{ cursor: 'help' }}>
                  {dashboardData.period_summary?.[getPeriodKey(activeTimeFrames.invoices)]?.invoice_discrepancy_count || 0}
                </span>
              </Tooltip>
            </div>
          </div>
          */}

          <div className="fd-expandable-section">
            <div className="fd-section-header">
              <div className="fd-section-title">Invoice Status</div>
            </div>
            <ul className="fd-detail-list">
              <li 
                className="fd-detail-item fd-detail-clickable"
                onClick={(e) => {
                  e.stopPropagation();
                  const startStr = dateRange?.start ? (dateRange.start.format ? dateRange.start.format('YYYY-MM-DD') : dateRange.start) : '';
                  const endStr = dateRange?.end ? (dateRange.end.format ? dateRange.end.format('YYYY-MM-DD') : dateRange.end) : '';
                  let url = '/document?tab=invoice';
                  if (startStr && endStr) {
                    url += `&start_date=${startStr}&end_date=${endStr}`;
                  } else if (startStr) {
                    url += `&start_date=${startStr}`;
                  } else if (endStr) {
                    url += `&end_date=${endStr}`;
                  }
                  handleNavigate(url);
                }}
              >
                <span className="fd-detail-label">Total Invoices</span>
                <Tooltip
                  title={
                    <div style={{ padding: '4px', maxWidth: '250px' }}>
                      <div style={{ marginBottom: '6px', fontSize: '14px', fontWeight: 'bold' }}>
                        {dashboardData.total_invoices || 0}
                      </div>
                      <div style={{ marginBottom: '6px', fontSize: '12px' }}>
                        Total number of all invoices processed.
                      </div>
                      <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '6px' }}>
                        Verification: Click this row to navigate to Document → Invoice and check the total table count for the selected date range.
                      </div>
                    </div>
                  }
                  arrow
                  placement="top"
                >
                  <span className="fd-detail-value" style={{ cursor: 'help' }}>{dashboardData.total_invoices || 0}
                            <span className="fd-arrow-icon">
        <IoIosArrowForward />
      </span>
                  </span>
                </Tooltip>
              </li>
              <li className="fd-detail-item" style={{padding: "8px 12px"}}>
                <span className="fd-detail-label">Pending Review</span>
                <Tooltip
                  title={
                    <div style={{ padding: '4px', maxWidth: '250px' }}>
                      <div style={{ marginBottom: '6px', fontSize: '14px', fontWeight: 'bold' }}>
                        {dashboardData.total_pending_invoices || 0}
                      </div>
                      <div style={{ marginBottom: '6px', fontSize: '12px' }}>
                        Invoices that are currently being reviewed or analyzed.
                      </div>
                      <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '6px' }}>
                        Verification: Go to Document → Invoice and filter by 'Pending' status, then check the row count.
                      </div>
                    </div>
                  }
                  arrow
                  placement="top"
                >
                  <span className="fd-detail-value" style={{ cursor: 'help' }}>{dashboardData.total_pending_invoices || 0}</span>
                </Tooltip>
              </li>
            </ul>
          </div>
        </div>

        {/* Location Spend Card - col-4 */}
        <div 
          className={`fd-finance-card ${animatedCards.includes(3) ? 'fd-animated' : ''}`}
          style={{ gridColumn: 'span 4' }}
          // className={`fd-finance-card fd-clickable ${animatedCards.includes(3) ? 'fd-animated' : ''}`}
          // onClick={() => handleCardClick('locations')}
        >
          <div className="fd-card-header">
            <h3 className="fd-card-title">Spend by Location</h3>
            <div className="fd-card-icon fd-icon-primary">
              <Globe size={18} />
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
            <Tooltip
              title={
                <div style={{ padding: '4px', maxWidth: '250px' }}>
                  <div style={{ marginBottom: '6px', fontSize: '14px', fontWeight: 'bold' }}>
                    {dashboardData.total_locations || 0}
                  </div>
                  <div style={{ marginBottom: '6px', fontSize: '12px' }}>
                    Total number of location IDs managed.
                  </div>
                  <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '6px' }}>
                    Verification: Click the 'View' button below to open the detailed Location Count modal and review the total.
                  </div>
                </div>
              }
              arrow
              placement="top"
            >
              <div className="fd-kpi-value" style={{ cursor: 'help' }}>{dashboardData.total_locations || 0}</div>
            </Tooltip>
            <div className="fd-kpi-label-l">Total location IDs</div>
          </div>
          
          <div className="fd-location-list">
            {dashboardData.top_3_countries && Object.entries(dashboardData.top_3_countries)
              .sort(([, a], [, b]) => b - a)
              .map(([country, count]) => (
              <Tooltip
                key={country}
                title={
                  <div style={{ padding: '4px', maxWidth: '250px' }}>
                    <div style={{ marginBottom: '6px', fontSize: '14px', fontWeight: 'bold' }}>
                      {count}
                    </div>
                    <div style={{ marginBottom: '6px', fontSize: '12px' }}>
                      Shows the top countries with the most active locations.
                    </div>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '6px' }}>
                      Verification: Click 'View' below and check the 'Location Count by Country' table.
                    </div>
                  </div>
                }
                arrow
                placement="top"
              >
                <div className="fd-location-item-l" style={{ cursor: 'help', padding: '2px 0', borderBottom: 'none', fontSize: '12px' }}>
                  <div className="fd-detail-label">
                    <span>{country}</span>
                  </div>
                  <span className="fd-location-count" style={{ fontSize: '12px' }}>{count} IDs</span>
                </div>
              </Tooltip>
            ))}
            {/* <div className="fd-location-item-l">
              <div className="fd-location-name">
                <span className="fd-flag">🌎</span>
                <span>Other Regions</span>
              </div>
              <span className="fd-location-count">
                {(dashboardData.total_locations || 0) - (dashboardData.top_3_countries 
                  ? Object.values(dashboardData.top_3_countries).reduce((a, b) => a + b, 0) 
                  : 0)} IDs
              </span>
            </div> */}
          </div>

          <div className="fd-expandable-section" style={{ marginTop: '5px', paddingTop: '2px', marginBottom: '5px' }}>
            <div className="fd-section-header" style={{marginTop:"10px", display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
              <div className="fd-section-title">Top Locations by Spend</div>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: '#f1f5f9',
                  borderRadius: '20px',
                  padding: '2px',
                }}
              >
                <Tooltip title="After taxes and discounts" arrow placement="top">
                  <Box
                    onClick={(e) => { e.stopPropagation(); setSpendLocationType('net'); }}
                    sx={{
                      padding: '4px 12px',
                      borderRadius: '18px',
                      fontSize: '0.75rem',
                      fontWeight: spendLocationType === 'net' ? 600 : 400,
                      color: spendLocationType === 'net' ? '#fff' : '#64748b',
                      backgroundColor: spendLocationType === 'net' ? '#7C2EA0' : 'transparent',
                      transition: 'all 0.2s ease-in-out',
                      cursor: 'pointer',
                    }}
                  >
                    NET
                  </Box>
                </Tooltip>
                <Tooltip title="Without taxes and discounts" arrow placement="top">
                  <Box
                    onClick={(e) => { e.stopPropagation(); setSpendLocationType('base'); }}
                    sx={{
                      padding: '4px 12px',
                      borderRadius: '18px',
                      fontSize: '0.75rem',
                      fontWeight: spendLocationType === 'base' ? 600 : 400,
                      color: spendLocationType === 'base' ? '#fff' : '#64748b',
                      backgroundColor: spendLocationType === 'base' ? '#7C2EA0' : 'transparent',
                      transition: 'all 0.2s ease-in-out',
                      cursor: 'pointer',
                    }}
                  >
                    BASE
                  </Box>
                </Tooltip>
              </Box>
            </div>
            <ul className="fd-detail-list">
              {(spendLocationType === 'net' ? dashboardData.total_net_amount_inv_by_country : dashboardData.total_amount_inv_by_country) && 
                Object.entries(spendLocationType === 'net' ? dashboardData.total_net_amount_inv_by_country : dashboardData.total_amount_inv_by_country)
                  .sort(([, a], [, b]) => (b?.total !== undefined ? b.total : b) - (a?.total !== undefined ? a.total : a))
                  .slice(0, 3)
                  .map(([country, amountObj]) => {
                    const totalValue = amountObj?.total !== undefined ? amountObj.total : amountObj;
                    const breakdown = amountObj?.currency_breakdown || {};
                    const currencies = Object.keys(breakdown);
                    const hasMultiple = currencies.length > 1;
                    const nonUsdCurrency = currencies.find(c => c !== 'USD');
                    
                    return (
                      <li key={country} className="fd-detail-item" style={{ padding: '2px 0' }}>
                        <span className="fd-detail-label">{country}</span>
                        <Tooltip
                          title={
                            (hasMultiple || nonUsdCurrency) ? (
                              <Box sx={{ p: 0.5, fontSize: "12px", minWidth: 220 }}>
                                <div style={{ fontWeight: 600, marginBottom: '4px', borderBottom: "1px solid rgba(255,255,255,0.2)", paddingBottom: '4px' }}>
                                  Calculation Breakdown
                                </div>
                                {nonUsdCurrency && (
                                  <>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                                      <span>{nonUsdCurrency} Amount:</span>
                                      <span>{nonUsdCurrency} {Number(breakdown[nonUsdCurrency].local).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                    </Box>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                                      <span>Converted to USD:</span>
                                      <span>USD {Number(breakdown[nonUsdCurrency].converted_to_usd).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                    </Box>
                                  </>
                                )}
                                {breakdown['USD'] && (
                                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                                    <span>USD Only Amount:</span>
                                    <span>+ USD {Number(breakdown['USD'].local).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                  </Box>
                                )}
                                <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1, pt: 0.5, borderTop: "1px solid rgba(255,255,255,0.2)", fontWeight: 600 }}>
                                  <span>Total USD:</span>
                                  <span>USD {Number(totalValue).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                </Box>
                                <div style={{ marginTop: '10px', marginBottom: '6px', fontSize: '12px', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '10px' }}>
                                  Shows the top 3 countries where you spend the most money.
                                </div>
                                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)' }}>
                                  Verification: Click 'View' below, switch to 'Spend by Country/Location' and verify the top amounts.
                                </div>
                              </Box>
                            ) : (
                              <div style={{ padding: '4px', maxWidth: '250px' }}>
                                <div style={{ marginBottom: '6px', fontSize: '14px', fontWeight: 'bold' }}>
                                  {Number(totalValue).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </div>
                                <div style={{ marginBottom: '6px', fontSize: '12px' }}>
                                  Shows the top 3 countries where you spend the most money.
                                </div>
                                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '6px' }}>
                                  Verification: Click 'View' below, switch to 'Spend by Country/Location' and verify the top amounts.
                                </div>
                              </div>
                            )
                          }
                          arrow
                          placement="top"
                        >
                          <span className="fd-detail-value" style={{ cursor: 'help', borderBottom: (hasMultiple || nonUsdCurrency) ? '1px dotted' : 'none' }}>USD {formatCurrency(totalValue)}</span>
                        </Tooltip>
                      </li>
                    );
                  })}
            </ul>
          </div>
                    <div className="fd-card-footer">
            <button
              type="button"
              className="fd-view-action fd-view-action--small"
              onClick={() => handleCardClick("locations")}
            >
              View Details
              <FiArrowRight size={11} className='fd-arrow-icon'/>
            </button>
          </div>
        </div>      </div>
      {/* End of main 5-card grid */}

      {/* Modal for Spend and Locations */}
      <CountryBreakdownModal
        open={openModal && (activeCard === 'spend' || activeCard === 'locations')}
        onClose={() => {
          setOpenModal(false);
          setActiveCard(null);
        }}
        initialFilters={{
          start_date: dateRange.start,
          end_date: dateRange.end,
          country: selectedCountry,
          location_id: selectedLoc,
          vendor_id: selectedVendor,
        }}
        formatNumber={internalFormatNumber}
        countryList={country}
        locationList={locations}
        canViewLocation={canViewLocation}
        title={activeCard === 'spend' ? "Spend by Country / Location" : "Location Count by Country / Location"}
      />

      <TopServicesModal
        open={topServicesModalOpen}
        onClose={() => setTopServicesModalOpen(false)}
        services={dashboardData?.grouped_service_stats?.top_discrepant_services || []}
        formatCurrency={formatCurrency}
      />

      {/* Modal for Missing Document Types */}
      <MissingDocTypesModal
        open={missingDocsModalOpen}
        onClose={() => setMissingDocsModalOpen(false)}
        totalMissingDiscrepancy={dashboardData.total_missing_doc_discrepancy}
        formatNumber={internalFormatNumber}
        initialFilters={{
          start_date: dateRange.start,
          end_date: dateRange.end,
          vendor_id: selectedVendor,
          location_id: selectedLoc,
          country: selectedCountry,
        }}
        countryList={country}
        locationList={locations}
        canViewLocation={canViewLocation}
      />

      {/* Modal for Missing Contract Breakdown */}
      <MissingContractBreakdownModal
        open={missingContractModalOpen}
        onClose={() => setMissingContractModalOpen(false)}
        sofStatus={sofStatus}
        formatNumber={internalFormatNumber}
        initialFilters={{
          vendor_id: selectedVendor,
        }}
      />

      <DiscrepancyBreakdownModal
        open={discrepancyModalOpen}
        onClose={() => setDiscrepancyModalOpen(false)}
        discrepancyType={discrepancyType}
        formatNumber={formatCurrency}
        initialFilters={{
          vendor_id: selectedVendor,
        }}
      />

      <RemovedServicesModal
        open={removedServicesModalOpen}
        onClose={() => setRemovedServicesModalOpen(false)}
        data={removedServicesData}
        loading={loadingRemovedServices}
        dateRange={dateRange}
      />

      <GroupedServicesDetailsModal
        open={groupedServicesModalOpen}
        onClose={() => setGroupedServicesModalOpen(false)}
        initialFilters={
          groupedServicesSearchFor === 'ARTIFACT_UNRESOLVED_CONTRACT'
            ? {
                start_date: dateRange.start,
                end_date: dateRange.end,
              }
            : {
                start_date: dateRange.start,
                end_date: dateRange.end,
                vendor_id: selectedVendor,
                location_id: selectedLoc,
              }
        }
        searchFor={groupedServicesSearchFor}
        formatNumber={internalFormatNumber}
      />
    </div>
  );
};

export default FinanceDashboard;
